import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay, tap, catchError, map } from 'rxjs/operators';
import {
  Company,
  CompanyCreateRequest,
  CompanyUpdateRequest,
  CompanyListItem,
  CompanySearchResponse,
  SalesforceCompany,
} from '../../../core/models/company.model';
import { PagedResult } from '../../../core/models/paged-result.model';
import { LoggingService } from '../../../core/services/logging.service';
import { BaseRepository } from './base.repository';
import envConfig from '../../../env-config';
import {
  getMockCompanyById,
  searchMockCompanies,
  checkArNumberExists,
} from '../../mocks/companies.mock';

/**
 * API response DTO from backend (direct DTO)
 */
interface CompanyApiDto {
  arNumber: string;
  companyName: string;
  customerTypeId?: number | null;
  customerTypeName: string | null;
  masterCompanyCode?: string | null;
  preferPrintSiteId: number;
  preferredPrintSiteName: string | null;
  outsourceConsent: boolean;
  isOutsourceOnly: boolean;
  isUltiBank: boolean;
  isPackingDetailDistribution: boolean;
  checkLayoutId: number;
  isBackOfficeSort: boolean;
  isCustom: boolean;
  isShippingUpCharge: boolean;
  customProcessFileName?: string | null;
  comments?: string | null;
  isManualCheck: boolean;
  isGroupRequests: boolean;
  createdDate?: string | null;
  createdBy?: string | null;
  modifiedDate?: string | null;
  modifiedBy?: string | null;
  subprocessorConsent?: string | null;
  arStatus?: string | null;
}

/**
 * Wrapper for API response from backend
 */
interface ApiResponse<T> {
  data: T;
  statusCode: number;
  message: string;
  isSuccess: boolean;
  errorType: string | null;
}

/**
 * Backend PagedResult DTO (uses 'page' instead of 'pageNumber')
 */
interface BackendPagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

@Injectable({ providedIn: 'root' })
export class CompaniesRepository extends BaseRepository {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggingService);
  private readonly apiUrl = envConfig.bffUrl ?? '';
  private readonly networkDelay = 300;

  /**
   * Gets paginated list of companies from the API.
   * @param filter Company filter with page, pageSize, and optional search
   * @returns Observable of paged company list items
   */
  getCompanies(filter: {
    page?: number;
    pageSize?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Observable<PagedResult<CompanyListItem>> {
    let params = new HttpParams()
      .set('page', (filter.page ?? 1).toString())
      .set('pageSize', (filter.pageSize ?? 20).toString());

    if (filter.search?.trim()) {
      params = params.set('search', filter.search.trim());
    }

    if (filter.sortBy) {
      params = params.set('sortBy', filter.sortBy);
    }

    if (filter.sortOrder) {
      params = params.set('sortOrder', filter.sortOrder);
    }

    this.logger.debug('CompaniesRepository: Fetching companies', {
      page: filter.page,
      pageSize: filter.pageSize,
      search: filter.search,
      sortBy: filter.sortBy,
      sortOrder: filter.sortOrder,
    });

    return this.http
      .get<BackendPagedResult<CompanyApiDto>>(`${this.apiUrl}/api/v1/company`, {
        params,
        context: this.authContext(),
      })
      .pipe(
        map(response => this.transformResponse(response)),
        tap(result =>
          this.logger.debug('CompaniesRepository: Companies fetched', {
            count: result.items.length,
            totalCount: result.totalCount,
          })
        ),
        catchError(error => {
          this.logger.error(
            'CompaniesRepository: Failed to fetch companies',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Transform API response to frontend model
   */
  private transformResponse(
    response: BackendPagedResult<CompanyApiDto>
  ): PagedResult<CompanyListItem> {
    return {
      items: response.items.map(dto => this.mapDtoToCompanyListItem(dto)),
      totalCount: response.totalCount,
      pageNumber: response.page,
      pageSize: response.pageSize,
      totalPages: response.totalPages,
      hasPreviousPage: response.hasPreviousPage,
      hasNextPage: response.hasNextPage,
    };
  }

  /**
   * Map backend DTO to frontend CompanyListItem model
   */
  private mapDtoToCompanyListItem(dto: CompanyApiDto): CompanyListItem {
    return {
      arNumber: dto.arNumber,
      name: dto.companyName,
      type: dto.customerTypeName,
      preferredPrintSite: dto.preferredPrintSiteName,
    };
  }

  /**
   * Map backend API DTO to frontend Company model
   */
  private mapApiDtoToCompany(dto: CompanyApiDto): Company {
    return {
      arNumber: dto.arNumber,
      name: dto.companyName,
      type: dto.customerTypeName ?? undefined,
      preferredPrintSite: dto.preferredPrintSiteName ?? undefined,
      customerTypeId: dto.customerTypeId ?? null,
      masterCompanyCode: dto.masterCompanyCode ?? null,
      outsourceConsent: dto.outsourceConsent,
      preferPrintSiteId: dto.preferPrintSiteId,
      isOutsourceOnly: dto.isOutsourceOnly,
      isUltiBank: dto.isUltiBank,
      isPackingDetailDistribution: dto.isPackingDetailDistribution,
      checkLayoutId: dto.checkLayoutId,
      isBackOfficeSort: dto.isBackOfficeSort,
      isCustom: dto.isCustom,
      isShippingUpCharge: dto.isShippingUpCharge,
      customProcessFileName: dto.customProcessFileName ?? null,
      comments: dto.comments ?? null,
      isManualCheck: dto.isManualCheck,
      isGroupRequests: dto.isGroupRequests,
      createdDate: dto.createdDate ?? null,
      createdBy: dto.createdBy ?? null,
      modifiedDate: dto.modifiedDate ?? null,
      modifiedBy: dto.modifiedBy ?? null,
    };
  }

  // Legacy methods using mock data (for other features not yet migrated to API)

  search(
    query: string,
    pageSize = 3,
    pageNumber = 1
  ): Observable<CompanySearchResponse> {
    const result = searchMockCompanies(query, pageSize, pageNumber);
    return of(result).pipe(delay(this.networkDelay));
  }

  getById(id: string): Observable<Company | null> {
    const company = getMockCompanyById(id);
    return of(company).pipe(delay(this.networkDelay));
  }

  getByArNumber(arNumber: string): Observable<Company | null> {
    this.logger.debug('CompaniesRepository: Fetching company by ArNumber', {
      arNumber,
    });

    return this.http
      .get<ApiResponse<CompanyApiDto>>(
        `${this.apiUrl}/api/v1/company/${arNumber}`,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        map(response => this.mapApiDtoToCompany(response.data)),
        tap(company =>
          this.logger.debug('CompaniesRepository: Company fetched', {
            arNumber: company.arNumber,
          })
        ),
        catchError(error => {
          if (error.status === 404) {
            this.logger.debug('CompaniesRepository: Company not found', {
              arNumber,
            });
            return of(null);
          }
          this.logger.error(
            'CompaniesRepository: Failed to fetch company',
            error
          );
          throw error;
        })
      );
  }

  /**
   * @deprecated Use getCompanies() instead for real API calls
   */
  getAll(
    pageSize = 10,
    pageNumber = 1
  ): Observable<{
    items: Company[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
  }> {
    // Redirect to real API - map CompanyListItem to Company (compatible shapes)
    return this.getCompanies({ page: pageNumber, pageSize }).pipe(
      map(result => ({
        items: result.items as unknown as Company[],
        totalCount: result.totalCount,
        pageNumber: result.pageNumber,
        pageSize: result.pageSize,
      }))
    );
  }

  /**
   * Creates a new company via the API.
   * @param request Company creation request with all required fields
   * @returns Observable of the created company
   */
  create(request: CompanyCreateRequest): Observable<Company> {
    this.logger.debug('CompaniesRepository: Creating company', {
      arNumber: request.arNumber,
    });

    return this.http
      .post<Company>(`${this.apiUrl}/api/v1/company`, request, {
        context: this.authContext(),
      })
      .pipe(
        tap(company =>
          this.logger.debug('CompaniesRepository: Company created', {
            arNumber: company.arNumber,
          })
        ),
        catchError(error => {
          this.logger.error(
            'CompaniesRepository: Failed to create company',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Updates an existing company via the API.
   * API returns 204 No Content on success.
   * @param arNumber The AR number of the company to update
   * @param request Company update request with editable fields
   * @returns Observable that completes when update succeeds (may not contain company data for 204 responses)
   */
  update(
    arNumber: string,
    request: CompanyUpdateRequest
  ): Observable<Company | null> {
    this.logger.debug('CompaniesRepository: Updating company', { arNumber });

    return this.http
      .put<Company | null>(
        `${this.apiUrl}/api/v1/company/${arNumber}`,
        request,
        {
          context: this.authContext(),
        }
      )
      .pipe(
        tap(() => {
          this.logger.debug('CompaniesRepository: Company updated', {
            arNumber: arNumber,
          });
        }),
        catchError(error => {
          this.logger.error(
            'CompaniesRepository: Failed to update company',
            error
          );
          throw error;
        })
      );
  }

  checkArNumberExists(
    arNumber: string,
    excludeId?: string
  ): Observable<boolean> {
    const exists = checkArNumberExists(arNumber, excludeId);
    return of(exists).pipe(delay(100));
  }

  /**
   * Searches for Salesforce companies that are NOT yet in FPS database.
   * Uses the new /api/v1/salesforce/accounts/search endpoint.
   * @param arNum Search term (AR number or company name, minimum 3 characters)
   * @returns Observable array of Salesforce companies available for adding
   */
  searchSalesforceCompanies(query: string): Observable<SalesforceCompany[]> {
    if (!query || query.trim().length < 3) {
      return of([]);
    }

    const params = new HttpParams().set('arNumber', query.trim());

    this.logger.debug('CompaniesRepository: Searching Salesforce companies', {
      query,
    });

    return this.http
      .get<ApiResponse<SalesforceCompany[]>>(
        `${this.apiUrl}/api/v1/salesforce/accounts/search`,
        {
          params,
          context: this.authContext(),
        }
      )
      .pipe(
        map(response => response.data || []),
        tap(results =>
          this.logger.debug('CompaniesRepository: Salesforce companies found', {
            count: results.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'CompaniesRepository: Failed to search Salesforce companies',
            error
          );
          return of([]); // Return empty array on error instead of throwing
        })
      );
  }
}
